var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Alert, Box, DialogContent, DialogFooter, DialogHeader, Typography } from '@pocketlaw/tetris';
import { useApproverDocumentAccess } from 'app/domains/approval/hooks/useApproverDocumentAccess';
import { useCreateApprovalRequest } from 'app/domains/approval/hooks/useCreateApprovalRequest';
import { useLastApprovalRequest } from 'app/domains/approval/hooks/useLastApprovalRequest';
import { ApprovalErrorSubcodes } from 'app/domains/esigning/utils';
import { ApolloError } from 'shared/domains/apollo/ApolloError';
import { ApprovalType } from 'shared/domains/apollo/generated/types';
import { documentFlush, usePreview } from 'shared/domains/documents';
import { CancelButton, FormDialog, SubmitButton, TextAreaField } from 'shared/domains/forms';
import { useApprovalWorkflow } from '../../../ApprovalWorkflowProvider';
import { ApproversPermissionList } from '../ApproversPermissionList';
const APPROVAL_ERROR_SUBCODES = [ApprovalErrorSubcodes.AlreadyPending, ApprovalErrorSubcodes.AlreadyApproved];
export function SendForApprovalModal(props) {
    const { approvalType, selectedApprovers, onClose } = props;
    const { documentId } = useApprovalWorkflow();
    const { createApprovalRequest } = useCreateApprovalRequest();
    const { refetch } = useLastApprovalRequest(documentId);
    const { isRtcAvailable } = usePreview();
    const selectedApproverUserIds = selectedApprovers.map((approver) => approver.userId);
    const { userIdsWithoutAccess, loading: loadingAccess, error: accessError, refetch: refetchAccess, } = useApproverDocumentAccess(selectedApproverUserIds);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [isApprovalStatusChanged, setIsApprovalStatusChanged] = useState(false);
    const filteredApproversWithoutPermission = selectedApprovers.filter(({ userId }) => userId && userIdsWithoutAccess.includes(userId));
    const canSubmit = filteredApproversWithoutPermission.length === 0;
    const handleClose = () => {
        if (isApprovalStatusChanged) {
            refetch();
        }
        onClose();
    };
    const handleError = (error) => {
        var _a, _b;
        setLoading(false);
        if (error instanceof ApolloError) {
            const subcode = (_b = (_a = error.graphQLErrors[0]) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.subcode;
            if (subcode && APPROVAL_ERROR_SUBCODES.includes(subcode)) {
                setIsApprovalStatusChanged(true);
                if (subcode === ApprovalErrorSubcodes.AlreadyPending) {
                    return setAlert({
                        appearance: 'info',
                        message: t({
                            comment: 'Handle ask for approval request dialog error message - Document approval already pending error',
                            message: 'Someone has already asked for approval for this document',
                        }),
                    });
                }
                if (subcode === ApprovalErrorSubcodes.AlreadyApproved) {
                    return setAlert({
                        appearance: 'info',
                        message: t({
                            comment: 'Handle ask for approval request dialog error message - Document approval already approved error',
                            message: 'This document has already been approved.',
                        }),
                    });
                }
            }
        }
        return setAlert({
            appearance: 'danger',
            message: t({
                comment: 'Handle ask for approval request dialog error message - Generic error message',
                message: 'Something went wrong while asking for approval, please try again.',
            }),
        });
    };
    const handleSubmit = (formValues) => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        setAlert(null);
        // Don't send selected approvers if approval type is all, Backend does not allow it
        const selectedUserIds = approvalType === ApprovalType.All ? undefined : selectedApproverUserIds;
        try {
            yield createApprovalRequest(documentId, userIdsWithoutAccess, selectedUserIds, formValues.message);
            if (isRtcAvailable) {
                documentFlush({ state: 'initialized' });
            }
            handleClose();
        }
        catch (error) {
            if (isRtcAvailable) {
                documentFlush({ state: 'not-initialized' });
            }
            handleError(error);
        }
    });
    return (<FormDialog size="sm" initialValues={{ message: '' }} state={{ loading: loadingAccess, error: Boolean(accessError) }} onClose={onClose} onSubmit={handleSubmit} onRefetch={refetchAccess}>
      <DialogHeader title={t({
            comment: 'Ask for approval dialog title',
            message: 'Ask for Approval',
        })}/>
      <DialogContent>
        <Box flexDirection="column" gap="xl">
          {alert && <Alert {...alert}/>}

          {canSubmit && (<Fragment>
              <Typography>
                <Trans comment="Ask for approval dialog body">
                  When the assigned approvers have approved the document you will be able to send it for eSigning
                </Trans>
              </Typography>
              <TextAreaField name="message" label={t({
                message: 'Message',
                comment: 'Approval Drawer - Ask for approval dialog - textarea label',
            })} placeholder={t({
                message: 'Your message...',
                comment: 'Approval Drawer - Ask for approval dialog - textarea placeholder',
            })}/>
            </Fragment>)}

          {!canSubmit && <ApproversPermissionList approvers={filteredApproversWithoutPermission}/>}
        </Box>
      </DialogContent>
      <DialogFooter>
        <CancelButton size="small" appearance="secondary" disabled={loading} onClick={onClose}>
          <Trans comment="Ask for approval dialog cancel button label">Cancel</Trans>
        </CancelButton>
        {canSubmit && (<SubmitButton size="small" appearance="primary" disabled={loading}>
            <Trans comment="Ask for approval dialog send for approval button label">Send for approval</Trans>
          </SubmitButton>)}
      </DialogFooter>
    </FormDialog>);
}
